import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosError,
} from 'axios';
import { tokenService } from '../services/tokenService';
import { GATEWAY_BASE_URL } from '../config/gateway';

const API_BASE_URL = `${GATEWAY_BASE_URL}/api/v1`;

function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

const instance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor: auth + institution + correlation ID
instance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const institutionId = tokenService.getInstitutionId();
    const token = await tokenService.getAccessToken();
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    if (institutionId && config.headers) {
      config.headers['X-Institution-ID'] = institutionId;
    }

    if (config.headers) {
      config.headers['X-Correlation-ID'] = generateCorrelationId();
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: error handling
instance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ message?: string; error?: string }>) => {
    const status = error.response?.status;

    if (status === 401) {
      tokenService.handleUnauthorized();
    }

    if (status === 403) {
      const event = new CustomEvent('api:forbidden', {
        detail: { message: 'You do not have permission to perform this action.' },
      });
      window.dispatchEvent(event);
    }

    if (status && status >= 500) {
      const event = new CustomEvent('api:server-error', {
        detail: {
          message:
            error.response?.data?.message ||
            error.response?.data?.error ||
            'An unexpected server error occurred. Please try again later.',
        },
      });
      window.dispatchEvent(event);
    }

    return Promise.reject(error);
  },
);

export const apiClient = {
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await instance.get<T>(url, { params });
    return response.data;
  },

  async getBlob(url: string): Promise<Blob> {
    const response = await instance.get<Blob>(url, { responseType: 'blob' });
    return response.data;
  },

  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await instance.post<T>(url, data);
    return response.data;
  },

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await instance.put<T>(url, data);
    return response.data;
  },

  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await instance.patch<T>(url, data);
    return response.data;
  },

  async delete<T>(url: string): Promise<T> {
    const response = await instance.delete<T>(url);
    return response.data;
  },
};
